import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Compressor from 'compressorjs';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { Modal, Form } from 'react-bootstrap';

import ImageCrop from '@components/LogoImageUpload/ImageCrop';
import LoaderBtn from '@components/LoaderBtn/loaderBtn';

import { routesPath } from '@constants/routesNavigate';
import { addUserSelectEditFiledProps } from '@constants/formFieldsArray';
import { searchTimeOut } from '@constants/general.const';

import { userDataObject, userSchema } from '@app/UserListing/Users/user.const';
import { updateIsLogOutUser } from '@app/Auth/Login/loginSlice';
import { inviteUser, updateInfo } from '@app/UserListing/Users/functions';
import { organizationDataSelectList } from '@app/CreateOrganization/step1/functions';
import UserFormFields from '@app/UserListing/Users/UserFormFields';
import { blobToBase64, checkFileSize } from '@utils/utilFunctions';
import '@app/Auth/Register/register.scss';

import { IUserProps } from './user.interface';
import {
    IListOption,
    ISelectListOptionSearchObj,
} from '../../../Interface/common';

const EditUserPopup = ({
    showPopup,
    handleCloseEvent,
    editData,
    listApiCall,
}: any) => {
    const dispatch = useDispatch<any>();
    const navigate = useNavigate();

    const { inviteUsers, organizationStep1Data } = useSelector(
        (state: any) => state,
    );

    const { isInviteUser, isUpdateInfo } = inviteUsers;
    const { isUserDataGet } = inviteUsers;

    const { isOrganizationDataSelectList } = organizationStep1Data;
    const [groupArr, setGroupArr] = useState<IListOption[]>([]);
    const [departmentArr, setDepartmentArr] = useState<IListOption[]>([]);
    const [officeArr, setOfficeArr] = useState<IListOption[]>([]);
    const [roleArr, setRoleArr] = useState<IListOption[]>([]);
    const [jobTitleArr, setJobTitleArr] = useState<IListOption[]>([]);
    const [searchObj, setSearchObj] = useState<ISelectListOptionSearchObj>({
        groupSearch: '',
        departmentSearch: '',
        officeSearch: '',
    });
    //  ask fore type

    const [profileImage, setProfileImage] = useState<any>('');
    const [cropModalOpen, setCropModalOpen] = useState<boolean>(false);
    const [userImage, setUserImage] = useState<any>('');
    const [extension, setExtension] = useState<any>('');
    const [image, setImage] = useState<any>();
    const userId = editData?.users_nano_id;

    const {
        handleSubmit,
        control,
        reset,
        setValue,
        formState: { errors },
    } = useForm<any>({
        resolver: yupResolver(userSchema),
        defaultValues: userDataObject,
        mode: 'onBlur',
    });

    useEffect(() => {
        if (showPopup) {
            getSelectList();
        }
    }, [showPopup]);

    useEffect(() => {
        if (editData) {
            reset(editData);
        }
        if (userId) {
            setValue('office_id', editData?.office_nano_id);
            setValue('department_id', editData?.department_nano_id);
            setValue('role_id', editData?.roles_nano_id);
            setValue('group_id', editData?.group_nano_id);
            setValue('jobTitle_id', editData?.job_nano_id);
            setUserImage(editData?.user_profile);
        }
    }, [editData]);

    useEffect(() => {
        const getData: NodeJS.Timeout = setTimeout(() => {
            getSelectList();
        }, searchTimeOut);
        return () => clearTimeout(getData);
    }, [searchObj]);

    const getSelectList = async () => {
        const { payload } = await dispatch(
            organizationDataSelectList({ searchObj: searchObj }),
        );
        const selectListData = payload?.data;

        if (selectListData?.message !== '' && selectListData?.status) {
            const mapDataOption = (
                list: any,
                valueKey: string,
                labelKey: string,
            ) =>
                list?.map((dataOption: any) => ({
                    value: dataOption?.[valueKey],
                    label: dataOption?.[labelKey],
                })) || [];

            setOfficeArr(
                mapDataOption(
                    selectListData?.data?.officeList,
                    'office_nano_id',
                    'office_name',
                ),
            );
            setDepartmentArr(
                mapDataOption(
                    selectListData?.data?.departmentList,
                    'department_nano_id',
                    'department_name',
                ),
            );
            setGroupArr(
                mapDataOption(
                    selectListData?.data?.groupList,
                    'group_nano_id',
                    'group_name',
                ),
            );
            setRoleArr(
                mapDataOption(
                    selectListData?.data?.roleList,
                    'roles_nano_id',
                    'role_name',
                ),
            );
            setJobTitleArr(
                mapDataOption(
                    selectListData?.data?.jobTitleList,
                    'job_nano_id',
                    'job_title',
                ),
            );
        } else {
            const errorMessage = payload?.message || 'An error occurred';
            toast.error(errorMessage);

            if (payload?.logout) {
                localStorage.removeItem('accessToken');
                dispatch(updateIsLogOutUser());
                navigate(routesPath?.login);
            }
        }
    };

    const saveWithCompressImage = async (blob: any) => {
        if (blob !== '') {
            new Compressor(blob, {
                quality: 0.6,
                maxHeight: 500,
                success: async (compressedResult: any) => {
                    const compressedImg: any = await blobToBase64(
                        compressedResult,
                    );
                    const isValidFileSize = checkFileSize(compressedImg);
                    if (isValidFileSize) {
                        setProfileImage(compressedImg);
                        setUserImage(compressedImg);
                    }
                },
            });
        }
    };

    const handleSave = async (imageData: any) => {
        const base64Response = await fetch(imageData);
        const blob = await base64Response.blob();
        saveWithCompressImage(blob);
    };

    const HandleChange = async (data: IUserProps) => {
        var sendData = {
            ...data,
            user_profile: profileImage ?? userImage,
            img_extension: extension,
        };
        const { payload } = await dispatch(
            userId
                ? updateInfo({
                      ...sendData,
                      user_id: userId,
                  })
                : inviteUser(sendData),
        );
        if (payload?.data?.message !== '' && payload?.data?.status) {
            handleCloseEvent();
            listApiCall();
        } else {
            if (payload?.logout) {
                localStorage.removeItem('accessToken');
                dispatch(updateIsLogOutUser());
                navigate(routesPath?.login);
            }
            if (payload?.message?.email?.[0] !== '') {
                toast.error(payload?.message?.email?.[0]);
            }
            if (payload?.message?.company_name?.[0] !== '') {
                toast.error(payload?.message?.company_name?.[0]);
            }
        }
    };

    const handleClickImageChange = (img: any, extensionValueOnClick: any) => {
        setImage(img);
        setExtension(extensionValueOnClick);
        setCropModalOpen(true);
    };

    return (
        <>
            <Modal
                show={showPopup}
                onHide={handleCloseEvent}
                backdrop="static"
                centered
                className="mfnw__userlisting_viewmodal"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fs-5 fw-semibold">
                        Edit User name
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {isUserDataGet ? (
                        <LoaderBtn />
                    ) : (
                        <Form onSubmit={handleSubmit(HandleChange)}>
                            <UserFormFields
                                control={control}
                                errors={errors}
                                officeArr={officeArr}
                                departmentArr={departmentArr}
                                groupArr={groupArr}
                                roleArr={roleArr}
                                jobTitleArr={jobTitleArr}
                                handleClickImageChange={handleClickImageChange}
                                userImage={userImage}
                                userData={editData}
                                addUserSelectFiledProps={
                                    addUserSelectEditFiledProps
                                }
                                setSearchObj={setSearchObj}
                                setValue={setValue}
                                onClose={() => {
                                    handleCloseEvent();
                                }}
                                editData={editData}
                                isOrganizationDataSelectList={
                                    isOrganizationDataSelectList
                                }
                                isInviteUser={isInviteUser}
                                isUpdateInfo={isUpdateInfo}
                            />
                        </Form>
                    )}
                </Modal.Body>
            </Modal>
            {cropModalOpen && (
                <ImageCrop
                    open={cropModalOpen}
                    handleClose={() => setCropModalOpen(false)}
                    image={image}
                    saveCroppedFile={(imageData: any) => handleSave(imageData)}
                />
            )}
        </>
    );
};

export default EditUserPopup;
